(() => {
  function initialize(root) {
    // Bind a component instance once, including theme-editor section reloads.
    if (root.dataset.originalReady) return;
    root.dataset.originalReady = 'true';
    const viewer = root.querySelector('[data-image-dialog]');
    const viewport = root.querySelector('[data-image-viewport]');
    const image = root.querySelector('[data-viewer-image]');
    const state = { zoom: 1, x: 0, y: 0, points: new Map(), distance: 0, trigger: null };

    // Share native dialog focus behavior across inquiry and image viewing.
    function openDialog(dialog, trigger) {
      state.trigger = trigger;
      dialog.showModal();
      (dialog.querySelector('[data-contact-success] h2, [role="alert"]') || dialog.querySelector('input:not([type="hidden"]), [data-dialog-close]'))?.focus();
    }
    function draw() {
      // Keep the zoomed image reachable without allowing it to drift offscreen.
      const maxX = Math.max(0, (image.width * state.zoom - viewport.clientWidth) / 2);
      const maxY = Math.max(0, (image.height * state.zoom - viewport.clientHeight) / 2);
      state.x = Math.min(maxX, Math.max(-maxX, state.x));
      state.y = Math.min(maxY, Math.max(-maxY, state.y));
      image.style.transform = 'translate(calc(-50% + ' + state.x + 'px),calc(-50% + ' + state.y + 'px)) scale(' + state.zoom + ')';
      root.querySelector('[data-zoom-level]').textContent = Math.round(state.zoom * 100) + '%';
    }
    function fit() {
      // Start from the available viewport while preserving the full image.
      const ratio = Math.min(viewport.clientWidth / image.naturalWidth, viewport.clientHeight / image.naturalHeight);
      image.style.width = Math.floor(image.naturalWidth * ratio) + 'px';
      image.style.height = Math.floor(image.naturalHeight * ratio) + 'px';
      state.zoom = 1; state.x = 0; state.y = 0;
      draw();
    }
    function loadImage() {
      // Expose loading and error states without retaining contact information.
      root.querySelector('[data-image-loading]').hidden = false;
      root.querySelector('[data-image-error]').hidden = true;
      image.hidden = true;
      image.src = state.trigger.dataset.imageUrl;
      image.alt = state.trigger.dataset.imageAlt || state.trigger.getAttribute('aria-label') || '';
    }
    function zoomBy(factor) {
      // All zoom input routes use the same bounds and transform.
      state.zoom = Math.min(6, Math.max(1, state.zoom * factor));
      draw();
    }
    function pointDistance() {
      // Measure the active two-finger gesture in viewport coordinates.
      const points = [...state.points.values()];
      return points.length < 2 ? 0 : Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    }

    // Delegate triggers so related stories share the same image/contact dialogs.
    root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-open-inquiry],[data-open-waitlist],[data-open-interest],[data-image-url]');
      if (!trigger) return;
      if (trigger.hasAttribute('data-image-url')) {
        openDialog(viewer, trigger); loadImage(); return;
      }
      const kind = trigger.hasAttribute('data-open-interest') ? 'open-interest' : trigger.hasAttribute('data-open-waitlist') ? 'waitlist' : 'inquiry';
      openDialog(root.querySelector('[data-contact-dialog="' + kind + '"]'), trigger);
    });
    root.querySelectorAll('dialog').forEach(dialog => {
      // Edition requests stay open until an explicit close or native form submission.
      dialog.querySelectorAll('[data-dialog-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
      const edition_request = ['waitlist', 'open-interest'].includes(dialog.dataset.contactDialog);
      dialog.addEventListener('cancel', event => { if (edition_request) event.preventDefault(); });
      dialog.addEventListener('click', event => { if (!edition_request && event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => { state.points.clear(); state.trigger?.focus(); });
    });

    // Leave contact transport with Shopify's native form and spam protection.
    root.querySelectorAll('[data-contact-dialog]').forEach(dialog => {
      const form = dialog.querySelector('form');
      const success = dialog.querySelector('[data-contact-success]');
      const kind = dialog.dataset.contactDialog;
      const draftKey = 'hc-contact-name:' + location.pathname + ':' + kind;
      const triggerKey = 'hc-contact-trigger:' + location.pathname + ':' + kind;
      const triggerAttribute = kind === 'open-interest' ? 'data-open-interest' : 'data-open-' + kind;
      const triggers = [...root.querySelectorAll('[' + triggerAttribute + ']')];
      let expected;
      let returnTrigger = triggers[0];
      try {
        expected = sessionStorage.getItem('hc-contact-kind');
        const savedTrigger = sessionStorage.getItem(triggerKey);
        if (savedTrigger !== null) returnTrigger = triggers[Number(savedTrigger)] || returnTrigger;
        sessionStorage.removeItem(triggerKey);
        const draft = JSON.parse(sessionStorage.getItem(draftKey) || 'null');
        sessionStorage.removeItem(draftKey);
        if (!success && dialog.querySelector('[role="alert"]') && draft && Date.now() - draft.time < 600000 && draft.time <= Date.now()) {
          form.elements['contact[name]'].value = draft.name;
        }
      } catch (_) {}
      if (!success && form) {
        const error = dialog.querySelector('[role="alert"]');
        if (error) error.tabIndex = -1;
        form.addEventListener('submit', event => {
          if (form.getAttribute('aria-busy') === 'true') { event.preventDefault(); return; }
          form.setAttribute('aria-busy', 'true');
          form.querySelector('[type="submit"]').disabled = true;
          try {
            sessionStorage.setItem('hc-contact-kind', kind);
            sessionStorage.setItem(triggerKey, String(triggers.indexOf(state.trigger)));
            sessionStorage.setItem(draftKey, JSON.stringify({ name: form.elements['contact[name]'].value, time: Date.now() }));
          } catch (_) {}
        });
        window.addEventListener('pageshow', () => {
          form.removeAttribute('aria-busy');
          form.querySelector('[type="submit"]').disabled = false;
        });
      }
      if (dialog.querySelector('[data-contact-result]') && expected === dialog.dataset.contactDialog) {
        openDialog(dialog, returnTrigger);
        try { sessionStorage.removeItem('hc-contact-kind'); } catch (_) {}
      }
    });

    // Announce image load success/failure and allow an explicit retry.
    image.addEventListener('load', () => {
      root.querySelector('[data-image-loading]').hidden = true;
      image.hidden = false;
      fit();
    });
    image.addEventListener('error', () => {
      root.querySelector('[data-image-loading]').hidden = true;
      root.querySelector('[data-image-error]').hidden = false;
    });
    root.querySelector('[data-image-retry]').addEventListener('click', loadImage);
    root.querySelector('[data-zoom-in]').addEventListener('click', () => zoomBy(1.5));
    root.querySelector('[data-zoom-out]').addEventListener('click', () => zoomBy(1 / 1.5));
    root.querySelector('[data-zoom-reset]').addEventListener('click', fit);

    // Mouse wheel and keyboard provide zoom and keyboard-only pan alternatives.
    viewport.addEventListener('wheel', event => {
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? 1.1 : 1 / 1.1);
    }, { passive: false });
    viewport.addEventListener('keydown', event => {
      const moves = { ArrowLeft: [50, 0], ArrowRight: [-50, 0], ArrowUp: [0, 50], ArrowDown: [0, -50] };
      if (moves[event.key]) {
        event.preventDefault(); state.x += moves[event.key][0]; state.y += moves[event.key][1]; draw();
      }
      if (event.key === '+' || event.key === '=') zoomBy(1.5);
      if (event.key === '-') zoomBy(1 / 1.5);
    });

    // Pointer capture supports both dragging and two-finger pinch without page scroll.
    viewport.addEventListener('pointerdown', event => {
      if (event.button !== 0) return;
      state.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      viewport.setPointerCapture(event.pointerId);
      state.distance = pointDistance();
    });
    viewport.addEventListener('pointermove', event => {
      const previous = state.points.get(event.pointerId);
      if (!previous) return;
      state.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (state.points.size === 1) {
        state.x += event.clientX - previous.x; state.y += event.clientY - previous.y; draw();
      } else {
        const distance = pointDistance();
        if (state.distance) zoomBy(distance / state.distance);
        state.distance = distance;
      }
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => {
      // End only the released pointer, preserving the other finger if present.
      viewport.addEventListener(name, event => { state.points.delete(event.pointerId); state.distance = pointDistance(); });
    });
    window.addEventListener('resize', () => { if (viewer.open && image.naturalWidth) fit(); });
  }

  // Support the normal storefront and Shopify's live section editor.
  document.querySelectorAll('[data-original-product]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-original-product]').forEach(initialize));
})();
